import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { NotePencil, Warning } from 'phosphor-react';

interface RenomearProcessoModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Nome PRETENDIDO atual (a empresa ainda não existe). */
  nomeAtual?: string | null;
  /**
   * O nome já foi aprovado na viabilidade? Quem resolve isso é o drawer, que tem o
   * checklist efetivo da UF — as chaves mudam entre registry e fallback.
   */
  nomeJaAprovado?: boolean;
  isLoading?: boolean;
  // eslint-disable-next-line no-unused-vars
  onConfirm: (novoNome: string) => void;
}

/**
 * Altera o nome PRETENDIDO — **só em constituição**.
 *
 * Por que existe: em constituição a empresa ainda não existe, e a consulta de viabilidade
 * pode reprovar exatamente o nome (idêntico ou semelhante a registro anterior é a recusa
 * mais comum da junta). Sem poder renomear, o operador tinha que arquivar o processo e
 * recriar, perdendo etapas, anexos e histórico.
 *
 * ⚠️ NÃO vale para alteração nem baixa, por decisão do usuário (2026-08-17). Nesses tipos a
 * razão social vem da consulta do CNPJ — é o nome REGISTRADO na junta. Deixar editar ali
 * convidaria a tela a divergir da realidade cadastral, e o campo deixaria de ser confiável
 * justamente onde ele é fato, não intenção. O gate está no drawer, que só monta este modal
 * quando `tipo === 'constituicao'`.
 *
 * ⚠️ O nome não tem coluna própria: é o TÍTULO DO CARD do kanban (o backend grava
 * `titulo=obj_in.razao_social` na criação e `ILegalizacaoUpdate` não o inclui). Por isso
 * renomear é `updateCard({ id, titulo })` — front puro, sem mudança de schema.
 */
export const RenomearProcessoModal: React.FC<RenomearProcessoModalProps> = ({
  isOpen,
  onClose,
  nomeAtual,
  nomeJaAprovado = false,
  isLoading = false,
  onConfirm,
}) => {
  const [nome, setNome] = useState('');

  // Recarrega do card a cada abertura — senão o nome de um processo vaza para o próximo.
  useEffect(() => {
    if (isOpen) setNome(nomeAtual ?? '');
  }, [isOpen, nomeAtual]);

  const limpo = nome.trim();
  const mudou = limpo !== (nomeAtual ?? '').trim();
  const podeSalvar = !!limpo && mudou;
  // Só alerta quando a mudança de fato invalida algo: nome aprovado + nome mudando.
  const alertaAprovacao = nomeJaAprovado && mudou;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent borderRadius="xl">
        <ModalHeader display="flex" alignItems="center" gap={2} fontSize="18px" fontWeight="700">
          <NotePencil size={18} weight="duotone" />
          Alterar nome pretendido
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <FormControl>
            <FormLabel fontSize="11px" fontWeight="700" color="gray.700">
              Nome pretendido
            </FormLabel>
            <Input
              value={nome}
              onChange={e => setNome(e.target.value)}
              placeholder="Nome da empresa"
              borderRadius="lg"
              fontSize="13px"
              autoFocus
            />
            <Text fontSize="11px" color="gray.400" mt={1}>
              A empresa ainda não existe — este é o nome levado à consulta de viabilidade.
            </Text>
          </FormControl>

          {/* ⚠️ A aprovação da viabilidade é POR NOME. Trocar o nome depois de aprovado torna
              a aprovação anterior inválida — a junta liberou outro nome. Mesma lógica do
              badge "reaprovar" do documento, que sinaliza quando o docx muda depois do
              aceite do cliente.
              Aviso em vez de desmarcar sozinho: o operador pode estar corrigindo um acento
              ou a grafia societária ("LTDA" → "Ltda"), caso em que a aprovação continua
              valendo. Quem sabe a diferença é ele, não a tela. */}
          {alertaAprovacao && (
            <Flex
              align="flex-start"
              gap={2}
              mt={4}
              px={3}
              py={2.5}
              bg="orange.50"
              borderWidth="1px"
              borderColor="orange.200"
              borderRadius="lg"
            >
              <Box mt="1px" flexShrink={0}>
                <Warning size={15} weight="fill" color="var(--chakra-colors-orange-500)" />
              </Box>
              <Text fontSize="11px" color="orange.800" lineHeight="1.5">
                A viabilidade já tem o <b>nome aprovado</b> marcado. A aprovação da junta vale para
                o nome anterior — se este for um nome <b>diferente</b>, desmarque o item na etapa de
                Viabilidade e consulte de novo.
              </Text>
            </Flex>
          )}
        </ModalBody>

        <ModalFooter gap={3}>
          <Button variant="ghost" borderRadius="full" onClick={onClose} isDisabled={isLoading}>
            Cancelar
          </Button>
          <Button
            colorScheme="purple"
            borderRadius="full"
            px={5}
            onClick={() => onConfirm(limpo)}
            isLoading={isLoading}
            isDisabled={!podeSalvar}
          >
            Salvar nome
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RenomearProcessoModal;
